import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Languages, Loader2, Mail, Plus, Send, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AuthService } from '@/services/core/auth'
import { StorageService } from '@/services/core/storage'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import { EmailTemplatesApi } from '@/services/networks/emailTemplatesApi'
import {
  EMAIL_CONTENT_FIELDS,
  EmailTranslationsApi,
  emailTranslationTypes,
  type EmailContentField,
  type EmailTranslation,
  type EmailTranslationDefault,
  type EmailTranslationInput,
} from '@/services/networks/emailTranslationsApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { networkApps } from '@/services/users/userRoles'
import { localeLabel, USER_LOCALES } from '@/data/userLocales'
import { formatDate } from '@/utils/format'
import { AppShell } from '../../components/AppShell'
import { BadgeToggleGroup } from '../../components/BadgeToggleGroup'
import { CodeEditor } from '../../components/CodeEditor'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { SearchSelect } from '../../components/SearchSelect'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { SwitchField } from '../../components/SwitchField'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useCurrentUser } from '../../contexts/UserContext'
import { useNetwork } from '../../contexts/NetworkContext'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import {
  ObjectStateToggle,
  useSaveObjectState,
  type ObjectState,
} from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

type ContentFlags = Record<EmailContentField, boolean>
type ContentValues = Record<EmailContentField, string>

/** Wording of its own, rather than blank or the API's default for the email type. */
function isCustomContent(value: string, defaultValue: string | undefined) {
  return value.trim() !== '' && value.trim() !== (defaultValue ?? '').trim()
}

// ── Inner form component ────────────────────────────────────────────────────

interface EmailTranslationEditFormProps {
  emailTranslation: EmailTranslation | null
  defaults: Record<string, EmailTranslationDefault>
  id: string
  isNew: boolean
}

function EmailTranslationEditForm({
  emailTranslation,
  defaults,
  id,
  isNew,
}: EmailTranslationEditFormProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const currentUser = useCurrentUser()
  const savedDefaults = defaults[emailTranslation?.messageDefinition ?? '']
  const savedCustom = Object.fromEntries(
    EMAIL_CONTENT_FIELDS.map((f) => [
      f,
      isCustomContent(emailTranslation?.[f] ?? '', savedDefaults?.[f]),
    ])
  ) as ContentFlags

  const form = useForm({
    defaultValues: {
      name: emailTranslation?.name ?? '',
      description: emailTranslation?.description ?? '',
      messageDefinition: emailTranslation?.messageDefinition ?? '',
      teamId: emailTranslation?.team?.id ?? '',
      applications: emailTranslation?.applications ?? [],
      locale: emailTranslation?.locale ?? '',
      userInterface: emailTranslation?.userInterface ?? '',
      emailTemplateId: emailTranslation?.emailTemplate?.id ?? '',
      replyTo: emailTranslation?.replyTo ?? '',
      custom: savedCustom,
      content: Object.fromEntries(
        EMAIL_CONTENT_FIELDS.map((f) => [f, savedCustom[f] ? emailTranslation![f] : ''])
      ) as ContentValues,
    },
    onSubmit: ({ value }) => {
      const typeDefaults = defaults[value.messageDefinition]
      // Any part left at the default is sent empty, so it follows the API's wording.
      const content = Object.fromEntries(
        EMAIL_CONTENT_FIELDS.map((f) => [
          f,
          value.custom[f] && isCustomContent(value.content[f], typeDefaults?.[f])
            ? value.content[f]
            : '',
        ])
      ) as ContentValues
      mutation.mutate({
        name: value.name,
        description: value.description,
        messageDefinition: value.messageDefinition,
        applications: value.applications,
        locale: value.locale,
        userInterface: value.userInterface,
        emailTemplate: value.emailTemplateId,
        replyTo: value.replyTo.trim(),
        ...content,
        ...(value.teamId || emailTranslation?.team
          ? { team: value.teamId ? { id: value.teamId } : null }
          : {}),
        objectState: (emailTranslation?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) =>
      EmailTranslationsApi.update(id, { name: emailTranslation?.name ?? '', objectState }),
    ['email-translation', id],
    ['email-translations']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)
  const values = useStore(form.store, (s) => s.values)
  const set = form.setFieldValue
  const typeDefaults = defaults[values.messageDefinition]

  const emailTypes = emailTranslationTypes(currentNetwork)
  const apps = networkApps(currentNetwork)
  const showApps = apps.length > 1 || !!emailTranslation?.applications.length
  // Teams own translations on a marketplace; elsewhere a team is only kept if one is already set.
  const showTeam = !!currentNetwork?.marketplacePlatform || !!emailTranslation?.team

  const { data: teams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ['teams', ''],
    queryFn: () => TeamsApi.getAll(),
    enabled: showTeam,
    staleTime: 5 * 60 * 1000,
  })
  const { data: userInterfaces = [], isLoading: userInterfacesLoading } = useQuery({
    queryKey: ['email-user-interfaces'],
    queryFn: () => EmailTranslationsApi.getUserInterfaces(),
    staleTime: Infinity,
  })
  const { data: allTemplates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['email-templates', ''],
    queryFn: () => EmailTemplatesApi.getAll(),
    staleTime: Infinity,
  })
  // A template the API has problems with can't be picked, unless it already is.
  const templateOptions = allTemplates
    .filter(
      (template) =>
        template.id === values.emailTemplateId ||
        (template.objectState === 'ACTIVE' && template.validationWarnings.length === 0)
    )
    .map((template) => ({ value: template.id, label: template.name }))

  const mutation = useMutation({
    mutationFn: (input: EmailTranslationInput) =>
      isNew ? EmailTranslationsApi.create(id, input) : EmailTranslationsApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['email-translation', id], data)
      queryClient.invalidateQueries({ queryKey: ['email-translations'] })
      markClean()
      if (isNew) navigate(`/admin/email-translations/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew
          ? t('emailTranslations.createdSuccess')
          : t('emailTranslations.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('emailTranslations.createFailed')
            : t('emailTranslations.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  // Only a support user masquerading as this user may send the test elsewhere.
  const masquerading = !!StorageService.getMasquerade()
  const [testRecipient, setTestRecipient] = useState(currentUser?.username ?? '')
  const sendTest = useMutation({
    mutationFn: () =>
      EmailTranslationsApi.sendTest(id, masquerading ? testRecipient.trim() : undefined),
    onSuccess: () =>
      toast.success(t('emailTranslations.testSent'), {
        description: t('emailTranslations.testSentDetail', {
          to: masquerading ? testRecipient.trim() : currentUser?.username,
        }),
        duration: 3000,
      }),
    onError: (error) =>
      toast.error(t('emailTranslations.testFailed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      }),
  })

  const changeType = (messageDefinition: string) => {
    set('messageDefinition', messageDefinition)
    // Parts at their default pick up the new type's default when switched on.
    EMAIL_CONTENT_FIELDS.forEach((f) => {
      if (!values.custom[f]) set(`content.${f}`, '')
    })
  }

  const toggleCustom = (f: EmailContentField, custom: boolean) => {
    set(`custom.${f}`, custom)
    if (custom && !values.content[f].trim()) set(`content.${f}`, typeDefaults?.[f] ?? '')
  }

  const crumbs = [
    { label: t('emailTranslations.title'), href: '/admin/email-translations' },
    {
      label: isNew
        ? t('emailTranslations.newEmailTranslation')
        : (emailTranslation?.name ?? t('emailTranslations.editTitle')),
    },
  ]

  // ── Content part: the default shown read-only, or the network's own wording ──

  const contentPart = (f: EmailContentField) => {
    const custom = values.custom[f]
    const defaultValue = typeDefaults?.[f] ?? ''
    const editor = (value: string, onChange?: (v: string) => void) =>
      f === 'emailSubject' ? (
        <Input value={value} readOnly={!onChange} onChange={(e) => onChange?.(e.target.value)} />
      ) : onChange ? (
        <CodeEditor value={value} onChange={onChange} variables="none" minHeight="140px" />
      ) : (
        <CodeEditor value={value} onChange={() => {}} variables="none" display maxHeight="320px" />
      )

    return (
      <div key={f} className="flex flex-col gap-2">
        <SwitchField
          label={t(`emailTranslations.customContentFields.${f}`)}
          description={
            custom
              ? t('emailTranslations.customContentOn')
              : t('emailTranslations.customContentOff')
          }
          checked={custom}
          onCheckedChange={(checked) => toggleCustom(f, checked)}
        />
        {custom ? (
          <form.Field name={`content.${f}`}>
            {(field) => editor(field.state.value, (v) => field.handleChange(v))}
          </form.Field>
        ) : defaultValue ? (
          editor(defaultValue)
        ) : (
          <p className="text-xs text-muted-foreground">{t('emailTranslations.noDefault')}</p>
        )}
      </div>
    )
  }

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={emailTranslation?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.emailTranslation')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('emailTranslations.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={emailTranslation!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {emailTranslation && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={emailTranslation.id} /> },
              {
                label: t('emailTranslations.emailType'),
                content: t(`emailTranslations.types.${emailTranslation.messageDefinition}`),
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(emailTranslation.createTime)}
                    updated={formatDate(emailTranslation.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="flex flex-col gap-3.5 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-3.5">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Languages} />
                  {t('emailTranslations.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <FieldLabel>{t('common.description')}</FieldLabel>
                    <form.Field name="description">
                      {(field) => (
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('emailTranslations.descriptionPlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <FieldLabel tooltip={t('emailTranslations.emailTypeHelp')}>
                      {t('emailTranslations.emailType')}
                    </FieldLabel>
                    <form.Field
                      name="messageDefinition"
                      validators={{
                        onSubmit: ({ value }) =>
                          value ? undefined : t('emailTranslations.emailTypeRequired'),
                      }}
                    >
                      {(field) => (
                        <>
                          <Select
                            label={t('emailTranslations.emailType')}
                            value={field.state.value}
                            onValueChange={changeType}
                          >
                            <SelectTrigger aria-invalid={field.state.meta.errors.length > 0}>
                              <SelectValue placeholder={t('emailTranslations.selectEmailType')}>
                                {field.state.value
                                  ? t(`emailTranslations.types.${field.state.value}`)
                                  : null}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {emailTypes.map((v) => (
                                <SelectItem key={v} value={v}>
                                  {t(`emailTranslations.types.${v}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {field.state.meta.errors[0] && (
                            <p className="text-xs text-destructive">
                              {field.state.meta.errors[0] as string}
                            </p>
                          )}
                        </>
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('emailTranslations.localeHelp')}>
                      {t('emailTranslations.locale')}
                    </FieldLabel>
                    <SearchSelect
                      value={values.locale}
                      onChange={(v) => set('locale', v)}
                      options={USER_LOCALES.map((code) => ({
                        value: code,
                        label: localeLabel(code, i18n.language),
                      }))}
                      placeholder={t('emailTranslations.allLocales')}
                      clearable
                    />
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('emailTranslations.userInterfaceHelp')}>
                      {t('emailTranslations.userInterface')}
                    </FieldLabel>
                    <SearchSelect
                      value={values.userInterface}
                      onChange={(v) => set('userInterface', v)}
                      options={userInterfaces.map((ui) => ({ value: ui.code, label: ui.url }))}
                      placeholder={t('emailTranslations.allUserInterfaces')}
                      fallbackLabel={values.userInterface}
                      clearable
                      loading={userInterfacesLoading}
                    />
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('emailTranslations.emailTemplateHelp')}>
                      {t('emailTranslations.emailTemplate')}
                    </FieldLabel>
                    <SearchSelect
                      value={values.emailTemplateId}
                      onChange={(v) => set('emailTemplateId', v)}
                      options={templateOptions}
                      placeholder={t('emailTranslations.defaultTemplate')}
                      fallbackLabel={emailTranslation?.emailTemplate?.name}
                      clearable
                      loading={templatesLoading}
                    />
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('emailTranslations.replyToHelp')}>
                      {t('emailTranslations.replyTo')}
                    </FieldLabel>
                    <form.Field name="replyTo">
                      {(field) => (
                        <Input
                          type="email"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('emailTranslations.replyToPlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                  {showTeam && (
                    <div className="p-field">
                      <FieldLabel tooltip={t('emailTranslations.teamHelp')}>
                        {t('emailTemplates.team')}
                      </FieldLabel>
                      <SearchSelect
                        value={values.teamId}
                        onChange={(v) => set('teamId', v)}
                        options={teams.map((team) => ({ value: team.id, label: team.name }))}
                        placeholder={t('emailTemplates.noTeam')}
                        fallbackLabel={emailTranslation?.team?.name}
                        clearable
                        loading={teamsLoading}
                      />
                    </div>
                  )}
                  {showApps && (
                    <div className="p-field full">
                      <FieldLabel
                        tooltip={t('emailTranslations.applicationsHelp')}
                        hint={t('emailTranslations.applicationsHint')}
                      >
                        {t('emailTranslations.applications')}
                      </FieldLabel>
                      <BadgeToggleGroup
                        options={apps.map((app) => ({
                          value: app,
                          label: t(`emailTranslations.apps.${app}`),
                        }))}
                        value={values.applications}
                        onChange={(v) => set('applications', v)}
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Mail} />
                  {t('emailTranslations.content')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {values.messageDefinition ? (
                  <div className="flex flex-col gap-5">
                    {!!typeDefaults?.parameters.length && (
                      <p className="text-xs text-muted-foreground">
                        {t('emailTranslations.variables')}{' '}
                        {typeDefaults!.parameters.map((p, i) => (
                          <span key={p}>
                            {i > 0 && ', '}
                            <code>{`{{${p}}}`}</code>
                          </span>
                        ))}
                      </p>
                    )}
                    {EMAIL_CONTENT_FIELDS.map(contentPart)}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t('emailTranslations.selectTypeFirst')}
                  </p>
                )}
              </CardContent>
            </Card>

            {!isNew && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Send} />
                    {t('emailTranslations.testEmail')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-3">
                    <p className="text-xs text-muted-foreground">
                      {t('emailTranslations.testEmailHelp')}
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      {masquerading ? (
                        <div className="p-field min-w-64 flex-1">
                          <FieldLabel>{t('emailTranslations.testRecipient')}</FieldLabel>
                          <Input
                            type="email"
                            value={testRecipient}
                            onChange={(e) => setTestRecipient(e.target.value)}
                          />
                        </div>
                      ) : (
                        <p className="flex-1 text-sm">
                          {t('emailTranslations.testSendsTo', { to: currentUser?.username ?? '' })}
                        </p>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        loading={sendTest.isPending}
                        disabled={masquerading && !testRecipient.trim()}
                        onClick={() => sendTest.mutate()}
                      >
                        <Send className="size-3.5" />
                        {t('emailTranslations.sendTest')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/email-translations')}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('emailTranslations.newEmailTranslation') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function EmailTranslationEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: emailTranslation,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['email-translation', id],
    queryFn: () => EmailTranslationsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })
  // The form needs the defaults up front to tell custom wording from default wording.
  const { data: defaults, isLoading: defaultsLoading } = useQuery({
    queryKey: ['email-translation-defaults'],
    queryFn: async () =>
      Object.fromEntries(
        (await EmailTranslationsApi.getDefaults()).map((d) => [d.messageDefinition, d])
      ) as Record<string, EmailTranslationDefault>,
    enabled: hasToken,
    staleTime: Infinity,
  })

  const crumbsLoading = [
    { label: t('emailTranslations.title'), href: '/admin/email-translations' },
    {
      label: isNew ? t('emailTranslations.newEmailTranslation') : t('emailTranslations.editTitle'),
    },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if ((isLoading && !isNew) || defaultsLoading) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return (
    <EmailTranslationEditForm
      key={id}
      emailTranslation={emailTranslation ?? null}
      defaults={defaults ?? {}}
      id={id!}
      isNew={isNew}
    />
  )
}
